import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'helpers/api';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import paths from 'routes/paths';
import { hasAccessControl } from 'components/authentication/accessControlMap';

const getFacilities = () => {
  try {
    const facilities = JSON.parse(localStorage.getItem('facilitiesData') || '[]');
    return Array.isArray(facilities) ? facilities : [];
  } catch (error) {
    return [];
  }
};

const unwrapRows = data => {
  const candidates = [
    data?.data?.attendance,
    data?.data?.rows,
    data?.data?.schedules,
    data?.attendance,
    data?.rows,
    data?.schedules,
    data?.data
  ];

  return candidates.find(Array.isArray) || [];
};

const getPatientName = row => {
  if (row.patient_name || row.patientName || row.name) {
    return row.patient_name || row.patientName || row.name;
  }

  const firstName = row.patient_firstname || row.patient_first_name || row.first_name || '';
  const lastName = row.patient_lastname || row.patient_last_name || row.last_name || '';
  return `${lastName}${lastName && firstName ? ', ' : ''}${firstName}`.trim() || 'Unknown Patient';
};

const normalizeAnswer = answer => {
  if (answer === true || answer === 1 || answer === '1' || String(answer).toLowerCase() === 'yes') {
    return 'yes';
  }

  if (answer === false || answer === 0 || answer === '0' || String(answer).toLowerCase() === 'no') {
    return 'no';
  }

  return '';
};

const formatDateTime = value =>
  value ? moment(value, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss', 'MM/DD/YYYY HH:mm:ss']).format('MM/DD/YY h:mm A') : '-';

const formatEventAnswer = answer => {
  const normalized = normalizeAnswer(answer);
  if (normalized === 'yes') return 'At Day Program';
  if (normalized === 'no') return 'Not at Day Program';
  return 'Not recorded';
};

const getResponseMessage = data =>
  data?.message || data?.error || data?.data?.message || 'Request completed.';

const DayProgramAttendance = () => {
  const hasDayProgramAccess = hasAccessControl('dayprogramactive');
  const facilityOptions = useMemo(() => [{ key: 'All Facilities', value: '' }, ...getFacilities()], []);
  const [selectedFacility, setSelectedFacility] = useState(facilityOptions[0]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordForm, setRecordForm] = useState({ patient: null, answer: 'yes' });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPatient, setHistoryPatient] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);

  const selectedDateForApi = moment(selectedDate).format('YYYY-MM-DD');

  const normalizeAttendanceRows = rows =>
    rows.map(row => {
      const answer = normalizeAnswer(row.answer ?? row.attendance_answer ?? row.day_program_answer);
      return {
        ...row,
        id: row.id || row.exception_id || row.attendance_id || `${row.patient_id}-${row.facility_id || ''}`,
        exceptionId: row.exception_id || row.attendance_id || row.id,
        patientId: row.patient_id || row.patientId,
        patientName: getPatientName(row),
        facilityId: row.facility_id || row.facilityId,
        answer,
        exceptedDoses: row.excepted_doses_count || row.excepted_doses || row.exception_doses || 0,
        missedDoses: row.missed_doses_count || row.missed_doses || 0,
        declaredBy: `${row.declared_by_first_name || ''} ${row.declared_by_last_name || ''}`.trim() || '-',
        declaredAt: row.declared_at || row.updated || row.updated_at || row.created_at,
        source: row.source || row.declared_source || '-'
      };
    });

  const fetchAttendance = async () => {
    if (!hasDayProgramAccess) return;

    setLoading(true);
    try {
      const { data } = await axios.post('/day-programs/list-attendance', {
        facility_id: selectedFacility?.value || '',
        date: selectedDateForApi,
        group_id: localStorage.getItem('groupId')
      });

      setAttendanceRows(normalizeAttendanceRows(unwrapRows(data)));
    } catch (error) {
      setAttendanceRows([]);
      toast.error(error?.response?.data?.message || 'Unable to load Day Program attendance.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, []);

  const patientOptions = useMemo(() => {
    const options = attendanceRows
      .filter(row => row.patientId)
      .map(row => ({ key: row.patientName, value: row.patientId, answer: row.answer }))
      .sort((a, b) => a.key.localeCompare(b.key));

    return options.filter((option, index, self) =>
      index === self.findIndex(item => String(item.value) === String(option.value))
    );
  }, [attendanceRows]);

  const summary = useMemo(() => {
    return attendanceRows.reduce(
      (acc, row) => {
        acc.total += 1;
        if (row.answer === 'yes') acc.yes += 1;
        else if (row.answer === 'no') acc.no += 1;
        else acc.pending += 1;
        acc.excepted += Number(row.exceptedDoses || 0);
        acc.missed += Number(row.missedDoses || 0);
        return acc;
      },
      { total: 0, yes: 0, no: 0, pending: 0, excepted: 0, missed: 0 }
    );
  }, [attendanceRows]);

  const openRecordModal = () => {
    setRecordForm({ patient: null, answer: 'yes' });
    setShowRecordModal(true);
  };

  const handleRecordAttendance = async () => {
    if (!recordForm.patient?.value) {
      toast.error('Select a patient.');
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.post('/day-programs/record-attendance', {
        patient_id: recordForm.patient.value,
        answer: recordForm.answer,
        source: 'web'
      });

      if (data?.already_recorded) {
        toast.info(getResponseMessage(data));
      } else {
        toast.success(getResponseMessage(data));
      }
      setShowRecordModal(false);
      fetchAttendance();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to record attendance.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeAnswer = async row => {
    const nextAnswer = row.answer === 'yes' ? 'no' : 'yes';
    const consequence =
      nextAnswer === 'yes'
        ? "mark today's eligible doses as Day Program exceptions."
        : "restore today's unactioned doses from Day Program exceptions.";

    if (!window.confirm(`Change ${row.patientName}'s answer to "${formatEventAnswer(nextAnswer)}"? This will ${consequence}`)) {
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.post('/day-programs/change-attendance-answer', {
        patient_id: row.patientId,
        exception_id: row.exceptionId,
        answer: nextAnswer,
        source: 'web'
      });

      toast.success(getResponseMessage(data));
      fetchAttendance();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to change attendance answer.');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async row => {
    setHistoryPatient(row);
    setHistoryEvents([]);
    setShowHistoryModal(true);
    setHistoryLoading(true);

    try {
      const { data } = await axios.get(`/day-programs/attendance-history/${row.patientId}?date=${selectedDateForApi}`);
      const events = data?.events || data?.data?.events || data?.data || [];
      setHistoryEvents(Array.isArray(events) ? events : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to load attendance history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const columns = [
    {
      accessorKey: 'patientName',
      header: 'Patient',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start fw-semibold' }
      }
    },
    {
      accessorKey: 'answer',
      header: 'Answer',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
      cell: ({ row: { original } }) => (
        <Badge bg={original.answer === 'yes' ? 'success' : original.answer === 'no' ? 'secondary' : 'warning'} className="px-2 py-1">
          {formatEventAnswer(original.answer)}
        </Badge>
      )
    },
    {
      accessorKey: 'exceptedDoses',
      header: 'Excepted',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'missedDoses',
      header: 'Missed',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'declaredBy',
      header: 'Declared By',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      }
    },
    {
      accessorKey: 'declaredAt',
      header: 'Declared At',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
      cell: ({ row: { original } }) => formatDateTime(original.declaredAt)
    },
    {
      accessorKey: 'source',
      header: 'Source',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      },
      cell: ({ row: { original } }) => (
        <span className="text-capitalize">
          <FontAwesomeIcon icon={original.source === 'mobile' ? 'mobile' : 'desktop'} className="me-1 text-600" />
          {original.source || '-'}
        </span>
      )
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      meta: {
        headerProps: { className: 'text-900 text-end' },
        cellProps: { className: 'text-end' }
      },
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <ButtonGroup size="sm">
          <Button variant="falcon-primary" disabled={saving || !original.answer} onClick={() => handleChangeAnswer(original)}>
            <FontAwesomeIcon icon="exchange-alt" className="me-1" />
            Change
          </Button>
          <Button variant="falcon-default" onClick={() => openHistory(original)}>
            <FontAwesomeIcon icon="history" className="me-1" />
            History
          </Button>
        </ButtonGroup>
      )
    }
  ];

  const table = useAdvanceTable({
    data: attendanceRows,
    columns,
    selection: false,
    sortable: true,
    pagination: true
  });

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Day Program Attendance', href: paths.dayProgramAttendance, isActive: true }
  ];

  if (!hasDayProgramAccess) {
    return (
      <>
        <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
        <Alert variant="warning" className="text-center">
          Day Program access is not active for this group.
        </Alert>
      </>
    );
  }

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      <Card className="mb-3 shadow-sm">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            <Col lg={3} md={6}>
              <Form.Group>
                <Form.Label>Facility</Form.Label>
                <ThemedSelect
                  closeMenuOnSelect
                  placeholder="Select Facility"
                  options={facilityOptions}
                  value={selectedFacility}
                  getOptionLabel={option => option.key}
                  getOptionValue={option => option.value}
                  onChange={setSelectedFacility}
                />
              </Form.Group>
            </Col>
            <Col lg={3} md={6}>
              <Form.Group>
                <Form.Label>Date</Form.Label>
                <DatePicker
                  selected={selectedDate}
                  onChange={date => setSelectedDate(date || new Date())}
                  dateFormat="MM/dd/yy"
                  className="form-control"
                />
              </Form.Group>
            </Col>
            <Col lg={3} md={6}>
              <Button variant="primary" onClick={fetchAttendance} disabled={loading} className="me-2">
                <FontAwesomeIcon icon="search" className="me-1" />
                Search
              </Button>
              <Button variant="falcon-default" onClick={openRecordModal}>
                <FontAwesomeIcon icon="plus" className="me-1" />
                Record
              </Button>
            </Col>
            <Col lg={3} md={6}>
              <AdvanceTableProvider {...table}>
                <AdvanceTableSearchBox placeholder="Search attendance..." />
              </AdvanceTableProvider>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        {[
          ['Patients', summary.total, 'hospital-user'],
          ['At Day Program', summary.yes, 'check-circle'],
          ['Not at Day Program', summary.no, 'minus-circle'],
          ['Pending', summary.pending, 'clock']
        ].map(([label, value, icon]) => (
          <Col md={3} sm={6} key={label}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="d-flex align-items-center justify-content-between p-3">
                <div>
                  <div className="text-600 fs-10 text-uppercase fw-semibold">{label}</div>
                  <div className="fs-4 fw-bold text-900">{value}</div>
                </div>
                <div className="rounded-circle bg-100 text-primary d-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
                  <FontAwesomeIcon icon={icon} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {loading ? (
        <ShimmerUI />
      ) : attendanceRows.length === 0 ? (
        <Alert variant="info" className="text-center">
          No Day Program attendance schedules found for this facility and date.
        </Alert>
      ) : (
        <AdvanceTableProvider {...table}>
          <Card className="mb-3 shadow-sm">
            <Card.Body className="p-3">
              <AdvanceTable
                headerClassName="bg-200 text-nowrap align-middle"
                rowClassName="text-nowrap align-middle"
                tableProps={{
                  size: 'sm',
                  striped: true,
                  hover: true,
                  className: 'fs-10 mb-0 overflow-hidden'
                }}
              />
            </Card.Body>
            <Card.Footer>
              <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
            </Card.Footer>
          </Card>
        </AdvanceTableProvider>
      )}

      <Modal show={showRecordModal} onHide={() => setShowRecordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Record Attendance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Patient</Form.Label>
            <ThemedSelect
              closeMenuOnSelect
              placeholder="Select patient"
              options={patientOptions}
              value={recordForm.patient}
              getOptionLabel={option => option.key}
              getOptionValue={option => option.value}
              onChange={patient => setRecordForm(prev => ({ ...prev, patient }))}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Answer</Form.Label>
            <div>
              <ButtonGroup>
                <Button
                  variant={recordForm.answer === 'yes' ? 'primary' : 'falcon-default'}
                  onClick={() => setRecordForm(prev => ({ ...prev, answer: 'yes' }))}
                >
                  At Day Program
                </Button>
                <Button
                  variant={recordForm.answer === 'no' ? 'primary' : 'falcon-default'}
                  onClick={() => setRecordForm(prev => ({ ...prev, answer: 'no' }))}
                >
                  Not at Day Program
                </Button>
              </ButtonGroup>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="falcon-default" onClick={() => setShowRecordModal(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleRecordAttendance} disabled={saving}>
            {saving && <Spinner size="sm" animation="border" className="me-2" />}
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{historyPatient?.patientName} Attendance History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : historyEvents.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No history events found for this date.
            </Alert>
          ) : (
            <div className="table-responsive scrollbar">
              <table className="table table-sm align-middle mb-0">
                <thead className="bg-200">
                  <tr>
                    <th>Time</th>
                    <th>Changed By</th>
                    <th>Change</th>
                    <th>Doses Affected</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {historyEvents.map((event, index) => (
                    <tr key={`${event.id || event.updated || index}`}>
                      <td>{formatDateTime(event.updated || event.created_at || event.declared_at)}</td>
                      <td>{event.updated_by_name || event.declared_by || event.user_name || '-'}</td>
                      <td>
                        {formatEventAnswer(event.from_answer || event.old_answer)}
                        <FontAwesomeIcon icon="arrow-right" className="mx-2 text-500" />
                        {formatEventAnswer(event.to_answer || event.answer || event.new_answer)}
                      </td>
                      <td>{event.doses_affected ?? event.excepted_doses_count ?? event.missed_doses_count ?? '-'}</td>
                      <td className="text-capitalize">{event.source || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-600 fs-10 mt-3">
                Today's totals are shown on the main row; first history events may not include dose counts.
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DayProgramAttendance;
